/**
 * The two weights a picture is sent in (D233, `wire/src/photo.rs`).
 *
 * **WebP on both sides.** The desktop writes it with libwebp and this side
 * writes it with the browser's own encoder, so the two clients no longer
 * merely agree on the ceilings — they agree on the bytes. That is the whole
 * reason the photo path moved off AVIF: a browser will not expose an AVIF
 * encoder to a canvas, and `canvas.toBlob("image/webp")` has been there since
 * Chrome 32, Firefox 96 and Safari 14.
 *
 * WebP has alpha, which JPEG (what this file used to write) did not, so a
 * transparent PNG now survives as itself instead of being flattened onto
 * white. The one browser worth worrying about is an old Safari, where
 * `toBlob` quietly hands back a PNG under the name it was asked for; that is
 * what {@link photoWeightsSupported} checks for, and a client that fails it
 * falls back to uploading the original.
 *
 * The numbers below mirror `echo_wire::photo`. They are not generated: ts-rs
 * emits types, not constants, and `MAX_ATTACHMENTS` is already mirrored by
 * hand a few files over. `photo.test.ts` pins them.
 */

/** Long side of the thumbnail, and the ceiling it has to fit. */
export const THUMB_DIM = 128;
export const THUMB_MAX_BYTES = 15 * 1024;
/** Long side of the preview — the weight a reader actually looks at. */
export const PREVIEW_DIM = 1024;
export const PREVIEW_MAX_BYTES = 300 * 1024;
/** An untouched original, which only travels when it is sent as a file. */
export const ORIGINAL_MAX_BYTES = 5 * 1024 * 1024;

/**
 * How an encode gives ground when it misses its ceiling: quality first, then
 * geometry — the same shape and the same rungs as the core's ladder, in the
 * canvas API's 0..1 quality rather than the encoder's 0..100. Resolution is
 * what a reader notices, so 0.68 of 1024 px beats 0.82 of 512 px at the same
 * weight.
 */
export const LADDER: ReadonlyArray<readonly [eighths: number, quality: number]> = [
  [8, 0.82],
  [8, 0.68],
  [8, 0.55],
  [6, 0.55],
  [4, 0.52],
  [2, 0.5],
  [1, 0.45],
];

/** Below this a picture is a swatch; the ladder stops and takes what it has. */
export const MIN_LADDER_DIM = 64;

export interface Weighed {
  blob: Blob;
  width: number;
  height: number;
}

export interface PhotoWeights {
  /** 1024 px, ≤ 300 KiB — the `file_id` of the attachment. */
  preview: Weighed;
  /** 128 px, ≤ 15 KiB — its `thumb_file_id`. */
  thumb: Weighed;
}

/**
 * Whether this browser can produce the weights at all: it has to decode the
 * original off the main thread *and* actually encode WebP.
 *
 * The second half is not paranoia. `toDataURL`/`toBlob` are specified to fall
 * back to PNG for a type they do not support, silently and under the requested
 * name — so a Safari 13 would upload 1024 px of lossless PNG as a "preview",
 * miss 300 KiB by a mile, and walk the whole ladder down to a swatch before
 * giving up. Better to send the original.
 */
export function photoWeightsSupported(): boolean {
  return typeof createImageBitmap === "function" && webpSupported();
}

/** Memoised: it costs a 1x1 canvas, and the answer cannot change. */
let webpEncodes: boolean | null = null;

function webpSupported(): boolean {
  if (webpEncodes === null) {
    try {
      const probe = document.createElement("canvas");
      probe.width = 1;
      probe.height = 1;
      webpEncodes = probe.toDataURL("image/webp").startsWith("data:image/webp");
    } catch {
      webpEncodes = false;
    }
  }
  return webpEncodes;
}

/**
 * Both weights of one picture, from one decode of the original.
 *
 * Throws if the browser cannot decode the file. Callers ask
 * {@link photoWeightsSupported} first and fall back to uploading the original
 * under `kind=photo` — the pre-D233 behaviour, and still a valid attachment:
 * the gateway reads a photo's geometry from its header and takes JPEG and PNG
 * for exactly this reason. What such a client loses is the thumbnail.
 */
export async function photoWeights(file: Blob): Promise<PhotoWeights> {
  const bitmap = await createImageBitmap(file);
  try {
    const preview = await encodeWithin(bitmap, PREVIEW_DIM, PREVIEW_MAX_BYTES);
    const thumb = await encodeWithin(bitmap, THUMB_DIM, THUMB_MAX_BYTES);
    return { preview, thumb };
  } finally {
    bitmap.close();
  }
}

/** The largest WebP of `bitmap` that fits both ceilings. */
async function encodeWithin(
  bitmap: ImageBitmap,
  maxDim: number,
  maxBytes: number,
): Promise<Weighed> {
  let best: Weighed | null = null;
  let drawnAt = -1;
  let canvas: Canvas | null = null;
  for (const [eighths, quality] of LADDER) {
    const dim = Math.max(1, Math.floor((maxDim * eighths) / 8));
    // Every rung past the first is smaller or cheaper, so one that has fallen
    // under the floor has nothing left to offer.
    if (dim < MIN_LADDER_DIM && best) break;
    const { width, height } = fitWithin(bitmap.width, bitmap.height, dim);
    // Consecutive rungs share a dim (the three quality steps at the top), and
    // the draw is the expensive half — so it happens once per dim.
    if (drawnAt !== dim || !canvas) {
      canvas = draw(bitmap, width, height);
      drawnAt = dim;
    }
    const blob = await toWebp(canvas, quality);
    best = { blob, width, height };
    if (blob.size <= maxBytes) break;
  }
  if (!best) throw new Error("the encode ladder is empty");
  return best;
}

/** `width`/`height` of the picture with its long side capped at `dim`.
 * Exported for its own test: everything around it needs a canvas. */
export function fitWithin(width: number, height: number, dim: number): { width: number; height: number } {
  const long = Math.max(width, height);
  if (long <= dim) return { width, height };
  const scale = dim / long;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

type Canvas = OffscreenCanvas | HTMLCanvasElement;

/**
 * `OffscreenCanvas` where there is one and a detached `<canvas>` where there
 * is not. Neither is attached to the document either way — this runs while the
 * composer is still on screen and must not disturb it.
 */
function draw(bitmap: ImageBitmap, width: number, height: number): Canvas {
  const canvas: Canvas = typeof OffscreenCanvas === "function"
    ? new OffscreenCanvas(width, height)
    : Object.assign(document.createElement("canvas"), { width, height });
  const ctx = canvas.getContext("2d") as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) throw new Error("this browser has no 2d canvas to scale a picture with");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // No white fill any more: WebP carries alpha, so a transparent PNG stays
  // transparent instead of being flattened the way the JPEG path had to.
  ctx.drawImage(bitmap, 0, 0, width, height);
  return canvas;
}

function toWebp(canvas: Canvas, quality: number): Promise<Blob> {
  if ("convertToBlob" in canvas) {
    return canvas.convertToBlob({ type: "image/webp", quality });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("the canvas produced no WebP"))),
      "image/webp",
      quality,
    );
  });
}
